import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthCardLayout } from '@/components/AuthCardLayout'
import { InputOTP } from '@/components/ui/input-otp'
import { usersService } from '@/services/users'
import { ROUTES } from '@/constants/routes'
import { getApiErrorMessage } from '@/utils/errors'
import { cn } from '@/lib/utils'

const RESEND_COOLDOWN_SECONDS = 10
const OTP_LENGTH = 4

export function ResetPasswordVerifyCodePage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const email = searchParams.get('email') ?? ''
  const [code, setCode] = useState('')
  const [resendCountdown, setResendCountdown] = useState(RESEND_COOLDOWN_SECONDS)
  const [hasResentOnce, setHasResentOnce] = useState(false)
  const [resendError, setResendError] = useState<string | null>(null)
  const [isResending, setIsResending] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)

  useEffect(() => {
    if (!email) {
      navigate(ROUTES.FORGOT_PASSWORD, { replace: true })
    }
  }, [email, navigate])

  useEffect(() => {
    if (code.length !== OTP_LENGTH) return
    const verifyCode = async () => {
      setVerifyError(null)
      setIsVerifying(true)
      try {
        await usersService.verifyPasswordResetCode({ email, code })
        navigate(ROUTES.RESET_PASSWORD_SET, { state: { email, code }, replace: true })
      } catch (err) {
        setVerifyError(getApiErrorMessage(err, t('resetPasswordVerify.invalidCode')))
        setCode('')
      } finally {
        setIsVerifying(false)
      }
    }
    verifyCode()
  }, [code, email, navigate, t])

  useEffect(() => {
    if (resendCountdown <= 0) return
    const timer = setInterval(() => setResendCountdown(c => c - 1), 1000)
    return () => clearInterval(timer)
  }, [resendCountdown])

  const handleResend = async () => {
    if (resendCountdown > 0 || !email) return
    setResendError(null)
    setIsResending(true)
    try {
      await usersService.requestPasswordReset({ email })
      setHasResentOnce(true)
      setResendCountdown(RESEND_COOLDOWN_SECONDS)
    } catch (err) {
      setResendError(getApiErrorMessage(err, t('forgotPassword.error')))
    } finally {
      setIsResending(false)
    }
  }

  if (!email) {
    return null
  }

  return (
    <AuthCardLayout
      title={t('resetPasswordVerify.title')}
      subtitle={t('resetPasswordVerify.instructions', { email })}
    >
      <div className="flex flex-col items-center">
        <label htmlFor="otp-code" className="mb-4 text-base font-semibold text-[#333333]">
          {t('resetPasswordVerify.codeLabel')}
        </label>
        <div className="mb-10 w-full">
          <InputOTP
            id="otp-code"
            length={OTP_LENGTH}
            value={code}
            onChange={v => setCode(v.replace(/\D/g, ''))}
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={isVerifying}
            autoFocus
          />
        </div>

        {verifyError && <p className="text-destructive mb-2 text-center text-sm">{verifyError}</p>}
        {resendError && <p className="text-destructive mb-2 text-center text-sm">{resendError}</p>}
        <p className="mb-2 text-base font-medium text-[#333333]">
          {t('resetPasswordVerify.resendPrompt')}
        </p>
        {resendCountdown > 0 ? (
          <p className="text-foreground mb-6 text-center text-base">
            <span className="font-normal">
              {hasResentOnce
                ? t('resetPasswordVerify.resendCountdownLabel')
                : t('resetPasswordVerify.resendAvailableInLabel')}{' '}
            </span>
            <span className="font-medium">{resendCountdown}</span>
          </p>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending}
            className={cn(
              'mb-6 w-full cursor-pointer rounded-lg py-2.5 text-base font-medium transition-opacity disabled:cursor-not-allowed disabled:opacity-60',
              isResending
                ? 'bg-[#E1E1E1] text-white'
                : 'bg-primary text-primary-foreground hover:opacity-90'
            )}
          >
            {isResending
              ? t('resetPasswordVerify.resending')
              : t('resetPasswordVerify.resendButton')}
          </button>
        )}
      </div>
    </AuthCardLayout>
  )
}
